import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn } from "@/lib/utils";
import { GeneratedAvatar } from "./GeneratedAvatar";
import { cva, type VariantProps } from "class-variance-authority";

const avatarVariants = cva(
  "relative flex shrink-0 overflow-hidden rounded-full",
  {
    variants: {
      size: {
        sm: "h-8 w-8 text-xs",
        md: "h-10 w-10 text-sm",
        lg: "h-14 w-14 text-base",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
);

interface AvatarProps extends VariantProps<typeof avatarVariants> {
  src?: string | null;
  /** Null when the subject has no known name; renders a neutral fallback. */
  name: string | null;
  className?: string;
}

function Avatar({ src, name, size, className }: AvatarProps) {
  return (
    <AvatarPrimitive.Root className={cn(avatarVariants({ size }), className)}>
      <AvatarPrimitive.Image
        src={src || undefined}
        // An unnamed avatar has no accessible name to give, and "" marks it
        // decorative rather than inventing alt text for a stranger.
        alt={name ?? ""}
        className="aspect-square h-full w-full object-cover"
      />
      {/* No photo: a drawn character seeded by the name, Reddit-style, in
          place of initials. The same name draws the same character on every
          surface. */}
      <AvatarPrimitive.Fallback className="flex h-full w-full">
        <GeneratedAvatar seed={name} />
      </AvatarPrimitive.Fallback>
    </AvatarPrimitive.Root>
  );
}

export { Avatar };
